import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {
  IFulfillVerificationRequestInterfaces,
} from '@toolify/server/src/controllers/VerificationController/validators/FulfillVerification/types/IFulfillVerificationRequestInterfaces'
import {
  IBaseVerificationTypeStrategyInterfaces,
} from '@toolify/server/src/services/VerificationService/types/IBaseVerificationTypeStrategyInterfaces'
import {
  IVerificationResponseInstance,
} from '@toolify/server/src/controllers/VerificationController/types/instances/IVerificationResponseInstance'
import {VerificationType} from '@toolify/server/src/models/mongoose/VerificationModel/enum/VerificationType'
import {
  IMailVerificationStrategyInterfaces,
} from '@toolify/server/src/services/VerificationService/strategies/verificationTypeStrategies/MailVerificationStrategy/types/IMailVerificationStrategyInterfaces'
import {
  ITwoFactorLoginVerificationStrategyInterfaces,
} from '@toolify/server/src/services/VerificationService/strategies/verificationTypeStrategies/TwoFactorLoginVerificationStrategy/types/ITwoFactorLoginVerificationStrategyInterfaces'
import {
  IResendVerificationRequestInterfaces,
} from '@toolify/server/src/controllers/VerificationController/validators/ResendVerification/types/IResendVerificationRequestInterfaces'
import {
  ICheckVerificationRequestInterfaces,
} from '@toolify/server/src/controllers/VerificationController/validators/CheckVerification/types/ICheckVerificationRequestInterfaces'

export const useVerificationApiStore = defineStore('verificationApi', {
  actions: {
    async fulfillVerification<Interfaces extends IBaseVerificationTypeStrategyInterfaces>(
      verificationId: string,
      secret: string,
    ): Promise<IFulfillVerificationRequestInterfaces<Interfaces>['response']> {
      const apiRequestStore = useApiRequestStore()
      const {verification} = await apiRequestStore.post<IFulfillVerificationRequestInterfaces<Interfaces>>(`/verification/${verificationId}/fulfill`, {
        data: {
          secret,
        },
      })
      return {
        verification: this.mapVerificationResponseInstance<Interfaces>(verification),
      }
    },

    async checkVerification(
      verificationId: string,
      code: string,
    ): Promise<ICheckVerificationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<ICheckVerificationRequestInterfaces>(`/verification/${verificationId}/check`, {
        data: {
          code,
        },
      })
    },

    async resendVerification(
      verificationId: string,
    ): Promise<IResendVerificationRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IResendVerificationRequestInterfaces>(`/verification/${verificationId}/resend`, {
        data: {},
      })
    },

    mapVerificationResponseInstance<Interfaces extends IBaseVerificationTypeStrategyInterfaces>(verification: IToJson<IVerificationResponseInstance<Interfaces>>): IVerificationResponseInstance<Interfaces> {
      return {
        ...verification,
        output: this.mapVerificationResponseInstanceOutput<Interfaces>(verification.type, verification.output),
      }
    },

    mapVerificationResponseInstanceOutput<Interfaces extends IBaseVerificationTypeStrategyInterfaces>(verificationType: VerificationType, output: IToJson<Interfaces['output']>): Interfaces['output'] {
      const strategies = {
        [VerificationType.MailAddress]: (output: IToJson<IMailVerificationStrategyInterfaces['output']>): IMailVerificationStrategyInterfaces['output'] => {
          return {
            ...output,
            expiresAt: new Date(output.expiresAt),
          }
        },
        [VerificationType.TwoFactorLogin]: (output: IToJson<ITwoFactorLoginVerificationStrategyInterfaces['output']>): ITwoFactorLoginVerificationStrategyInterfaces['output'] => {
          return {
            ...output,
            expiresAt: new Date(output.expiresAt),
          }
        },
      }
      const strategy = strategies[verificationType]
      return strategy ? strategy(output) : (output as Interfaces['output'])
    },
  },
})
